import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Grid,
  List,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Typography,
  ListItemButton,
  Divider,
  FormControlLabel // Nuevo import
} from '@mui/material';

interface ModalTransferListProps {
  open: boolean;
  onClose: () => void;
  onSave: (selectedItems: any[]) => void;
  type: 'personal' | 'equipo' | 'produccion';
  data: any[];
  enableIncompleteFilter?: boolean; // Nueva propiedad opcional
}

const ModalTransferList: React.FC<ModalTransferListProps> = ({
  open,
  onClose,
  onSave,
  type,
  data,
  enableIncompleteFilter // Recibimos la propiedad
}) => {
  const [left, setLeft] = useState<any[]>([]);
  const [right, setRight] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [checked, setChecked] = useState<any[]>([]);

  // Nuevo estado para el checkbox de filtro
  const [showOnlyIncomplete, setShowOnlyIncomplete] = useState(false);

  useEffect(() => {
    if (open) {
      setLeft(data);
      setRight([]);
      setChecked([]);
      setSearchTerm('');
      setShowOnlyIncomplete(false); // Reseteamos el filtro al abrir
    }
  }, [open, data]);

  // Filtrar elementos según la búsqueda Y el checkbox de incompletos
  const filteredLeft = left.filter((item) => {
    const search = searchTerm.toLowerCase();

    if (type === 'personal') {
      return (
        (item.name && item.name.toLowerCase().includes(search)) ||
        (item.rut && item.rut.toLowerCase().includes(search)) ||
        (item.firstName && item.firstName.toLowerCase().includes(search)) ||
        (item.lastName && item.lastName.toLowerCase().includes(search)) ||
        (item.position && item.position.toLowerCase().includes(search))
      );
    }

    if (type === 'equipo') {
      return (
        (item.name && item.name.toLowerCase().includes(search)) ||
        (item.vehicleNumber && item.vehicleNumber.toLowerCase().includes(search))
      );
    }

    if (type === 'produccion') {
      const matchesSearch = item.towerNumber && item.towerNumber.toLowerCase().includes(search);

      // Lógica del nuevo filtro:
      // Si el filtro está habilitado Y el check activado, ocultamos los que tengan >= 99.9%
      if (enableIncompleteFilter && showOnlyIncomplete) {
        const percentage = item.cumulativePercentage || 0;
        if (percentage >= 99.9) {
          return false;
        }
      }

      return matchesSearch;
    }

    return false;
  });

  const numberOfChecked = (items: any[]) => checked.filter((value) => items.indexOf(value) !== -1).length;

  const handleToggleAll = (items: any[]) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(checked.filter((value) => items.indexOf(value) === -1));
    } else {
      setChecked([...checked, ...items.filter((value) => checked.indexOf(value) === -1)]);
    }
  };

  const handleCheckedRight = () => {
    const selected = left.filter((item) => isItemSelected(item));
    setRight(right.concat(selected));
    const remaining = left.filter((item) => !isItemSelected(item));
    setLeft(remaining);
    setChecked([]);
  };

  const handleCheckedLeft = () => {
    const selected = right.filter((item) => isItemSelected(item));
    setLeft(left.concat(selected));
    const remaining = right.filter((item) => !isItemSelected(item));
    setRight(remaining);
    setChecked([]);
  };

  const isItemSelected = (item: any) => checked.indexOf(item) !== -1;

  const handleToggle = (item: any) => () => {
    const currentIndex = checked.indexOf(item);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(item);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const customList = (items: any[], side: 'left' | 'right') => (
    <List dense component="div" role="list">
      <ListItemButton
        onClick={handleToggleAll(items)}
      >
        <ListItemIcon>
          <Checkbox
            checked={numberOfChecked(items) === items.length && items.length !== 0}
            indeterminate={numberOfChecked(items) > 0 && numberOfChecked(items) < items.length}
            disabled={items.length === 0}
            inputProps={{ 'aria-label': 'select all items' }}
          />
        </ListItemIcon>
        <ListItemText
          primary={`${numberOfChecked(items)}/${items.length} seleccionados`}
        />
      </ListItemButton>
      {items.map((item, index) => {
        const labelId = `transfer-list-item-${side}-${index}`;
        return (
          <ListItemButton
            key={labelId}
            role="listitem"
            onClick={handleToggle(item)}
          >
            <ListItemIcon>
              <Checkbox
                checked={isItemSelected(item)}
                tabIndex={-1}
                disableRipple
                inputProps={{ 'aria-labelledby': labelId }}
              />
            </ListItemIcon>
            <ListItemText
              id={labelId}
              primary={
                type === 'personal'
                  ? `${item.name} ${item.firstName} (${item.position})`
                  : type === 'equipo'
                    ? `${item.name} (${item.vehicleNumber})`
                    : item.towerNumber
              }
            />
          </ListItemButton>
        );
      })}
    </List>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Agregar {type === 'personal' ? 'Personal' : type === 'equipo' ? 'Equipo' : 'Producción'}</DialogTitle>
      <DialogContent>
        <TextField
          label="Buscar"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          sx={{ mb: 1, mt: 2 }}
        />

        {/* Checkbox para filtrar solo pendientes, visible si enableIncompleteFilter es true */}
        {enableIncompleteFilter && (
          <FormControlLabel
            control={
              <Checkbox
                checked={showOnlyIncomplete}
                onChange={(e) => setShowOnlyIncomplete(e.target.checked)}
                color="primary"
              />
            }
            label="Ocultar torres completadas (100%)"
            sx={{ mb: 2, ml: 1 }}
          />
        )}

        <Grid container spacing={2} justifyContent="center" alignItems="center">
          {/* Lista izquierda */}
          <Grid item xs={5} sx={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Typography variant="subtitle1" align="center" sx={{ mb: 1 }}>
              Disponibles para agregar
            </Typography>
            {customList(filteredLeft, 'left')}
          </Grid>

          {/* Contenedor central para las flechas */}
          <Grid item xs={2}>
            <Grid
              container
              direction="column"
              alignItems="center"
              justifyContent="center"
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: 'white',
                padding: 1,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCheckedRight}
                disabled={checked.filter((value) => left.indexOf(value) !== -1).length === 0}
                aria-label="move selected to right"
              >
                &gt;
              </Button>
              <Divider sx={{ my: 1 }} />
              <Button
                variant="outlined"
                onClick={handleCheckedLeft}
                disabled={checked.filter((value) => right.indexOf(value) !== -1).length === 0}
                aria-label="move selected to left"
              >
                &lt;
              </Button>
            </Grid>
          </Grid>

          {/* Lista derecha */}
          <Grid item xs={5} sx={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Typography variant="subtitle1" align="center" sx={{ mb: 1 }}>
              Seleccionados para agregar
            </Typography>
            {customList(right, 'right')}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          onClick={() => {
            onSave(right);
            onClose();
          }}
          variant="contained"
          color="primary"
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalTransferList;